// INDEX_SPLIT_B2_v1 (07.09.2026) -- Leistungen: PhasesEditor, ServicesPage, TierPricingTab
// 1:1 aus app.jsx verschoben (Reihenfolge unverändert), nur `export` ergänzt und Imports erzeugt (scripts/split_frontend.mjs).
import { Check, Clock, Plus, Trash2, X, _, useEffect, useState } from "../core/vendor.js";
import { api } from "../core/api.js";
import { COLORS, defaultCategories, isKosmetikSalon } from "../core/config.jsx";
import { Btn, Card, Input, Modal, Select } from "../core/ui.jsx";
import { Header } from "../layout/layout.jsx";
import { svcTintOf } from "../core/svcaudience.js"; // SERVICE_AUDIENCE_COLORS_v1


// ============================================================
// SERVICE_PHASES_UI_v1: Modal zum Bearbeiten der Service-Phasen
// PHASE_EDITOR_SYNC_v1: Start-Minuten werden automatisch berechnet (Phasen folgen lückenlos),
// Mindestdauer 5 Min, Gesamtdauer wird beim Speichern in die Dienstleistung übernommen.
export const PhasesEditor = ({ serviceId, serviceName, baseDuration, onClose, onSaved }) => {
  const [phases, setPhases] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (serviceId === null || serviceId === undefined) return;
    setLoading(true);
    fetch("/api/services/" + serviceId + "/phases", { headers: { "Authorization": "Bearer " + sessionStorage.getItem("sp_token") } })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => { setPhases((d.phases || []).map((p, i) => ({...p, _key: i}))); setLoading(false); })
      .catch(e => { setError("Laden fehlgeschlagen: " + e); setLoading(false); });
  }, [serviceId]);
  const updatePhase = (idx, field, value) => setPhases(ps => ps.map((p, i) => i === idx ? { ...p, [field]: field === "label" ? value : Number(value) } : p));
  const addPhase = () => setPhases(ps => [...(ps || []), { phase_no: (ps || []).length + 1, start_offset_min: 0, duration_min: 15, busy_pct: 100, label: "Neue Phase", is_active: true, _key: Date.now() }]);
  const removePhase = (idx) => setPhases(ps => ps.filter((_, i) => i !== idx).map((p, i) => ({ ...p, phase_no: i + 1 })));
  // PHASE_EDITOR_SYNC_v1 -- Start-Minuten automatisch: jede Phase beginnt, wo die vorige endet
  const withOffsets = (ps) => {
    let off = 0;
    return (ps || []).map(p => {
      const dur = Math.max(0, Number(p.duration_min) || 0);
      const out = { ...p, start_offset_min: off, duration_min: dur };
      if (p.is_active !== false) off += dur;
      return out;
    });
  };
  const totalDur = withOffsets(phases).reduce((s, p) => s + (p.is_active !== false ? p.duration_min : 0), 0);
  const save = async () => {
    const bad = (phases || []).find(p => (Number(p.duration_min) || 0) < 5);
    if (bad) { setError("Jede Phase braucht mindestens 5 Minuten Dauer."); return; }
    setSaving(true); setError(null);
    try {
      const body = withOffsets(phases).map(({_key, ...rest}) => rest);
      const res = await fetch("/api/services/" + serviceId + "/phases", {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + sessionStorage.getItem("sp_token") },
        body: JSON.stringify({ phases: body })
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || ("HTTP " + res.status)); }
      const resData = await res.json().catch(() => ({}));
      if (onSaved) onSaved(resData.base_duration || null, body);
      onClose();
    } catch (e) { setError("Speichern fehlgeschlagen: " + e.message); } finally { setSaving(false); }
  };
  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: COLORS.card, borderRadius: 12, padding: 24, maxWidth: 720, width: "92%", maxHeight: "86vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18 }}>Phasen bearbeiten</h3>
            <div style={{ fontSize: 13, color: COLORS.textLight, marginTop: 2 }}>{serviceName} - Basisdauer {baseDuration} Min.</div>
          </div>
          <Btn size="sm" variant="secondary" onClick={onClose}><X size={14} /></Btn>
        </div>
        <div style={{ fontSize: 12, color: COLORS.textLight, marginBottom: 12, lineHeight: 1.4 }}>Phasen unterteilen einen Service in Schritte (z.B. Auftragen → Einwirken → Ausspülen). Bei <strong>„beschäftigt"</strong> ist der Mitarbeiter gebunden; bei <strong>„Einwirkzeit (frei)"</strong> ist er frei und kann in dieser Zeit einen anderen Kunden bedienen.</div>
        {loading ? <div style={{ padding: 20, textAlign: "center" }}>Laedt...</div> : (
          <>
            {error && <div style={{ background: "rgba(239,68,68,0.12)", color: COLORS.danger, padding: 10, borderRadius: 6, marginBottom: 10, fontSize: 13 }}>{error}</div>}
            <div style={{ display: "grid", gridTemplateColumns: "50px 1fr 80px 80px 150px 30px", gap: 8, marginBottom: 6, fontSize: 11, color: COLORS.textLight, fontWeight: 600 }}>
              <div style={{ textAlign: "center" }}>#</div><div>Label</div><div>Beginn</div><div>Dauer (Min)</div><div>Mitarbeiter</div><div></div>
            </div>
            {(phases || []).length === 0 && <div style={{ padding: 20, textAlign: "center", color: COLORS.textLight, fontSize: 13 }}>Keine Phasen definiert. Für Standardservices wie Schnitt nicht nötig.</div>}
            {withOffsets(phases).map((p, idx) => (
              <div key={p._key} style={{ display: "grid", gridTemplateColumns: "50px 1fr 80px 80px 150px 30px", gap: 8, alignItems: "center", marginBottom: 8, padding: 8, background: COLORS.bg, borderRadius: 6 }}>
                <strong style={{ textAlign: "center" }}>#{p.phase_no}</strong>
                <input type="text" value={p.label || ""} onChange={e => updatePhase(idx, "label", e.target.value)} style={{ padding: "6px 8px", border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 13 }} />
                {/* PHASE_EDITOR_SYNC_v1 -- Beginn wird automatisch berechnet, kein Eingabefeld mehr */}
                <div style={{ fontSize: 12, color: COLORS.textLight, textAlign: "center" }} title="wird automatisch berechnet">ab Min. {p.start_offset_min}</div>
                <input type="number" min={5} step={5} value={p.duration_min} onChange={e => updatePhase(idx, "duration_min", e.target.value)} style={{ padding: "6px 8px", border: `1px solid ${(Number(p.duration_min) || 0) < 5 ? COLORS.danger : COLORS.border}`, borderRadius: 4, fontSize: 13 }} />
                <select value={p.busy_pct} onChange={e => updatePhase(idx, "busy_pct", e.target.value)} style={{ padding: "6px 8px", border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 13 }}>
                  <option value={100}>beschäftigt</option>
                  <option value={0}>Einwirkzeit (frei)</option>
                </select>
                <button onClick={() => removePhase(idx)} style={{ background: "none", border: "none", color: COLORS.danger, cursor: "pointer" }}><Trash2 size={14} /></button>
              </div>
            ))}
            {(phases || []).length > 0 && (
              <div style={{ marginTop: 10, padding: "8px 10px", background: COLORS.bg, borderRadius: 6, fontSize: 12.5 }}>
                Gesamtdauer aus Phasen: <strong>{totalDur} Min</strong> — wird beim Speichern als neue Dauer der Dienstleistung übernommen.
                <span style={{ color: COLORS.textLight }}> Gilt nur für neue Termine; bestehende Termine bleiben unverändert.</span>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "space-between", marginTop: 16 }}>
              <Btn size="sm" variant="secondary" onClick={addPhase}><Plus size={12} /> Phase hinzufügen</Btn>
              <div style={{ display: "flex", gap: 8 }}>
                <Btn size="sm" variant="secondary" onClick={onClose}>Abbrechen</Btn>
                <Btn size="sm" variant="primary" onClick={save} disabled={saving}>{saving ? "Speichert..." : "Speichern"}</Btn>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// PAGE: Services (editierbar + hinzufügbar)
// ============================================================
export const ServicesPage = ({ services, setServices }) => {
  const [editId, setEditId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  // SERVICE_CREATE_FULL_v1 -- erweiterter Neu-Service mit Subkategorie + Längen + Phasen
  const [newSvc, setNewSvc] = useState({
    name: "", category: isKosmetikSalon() ? "Gesicht" : "Schnitt", subcategory: "", basePrice: 30, baseDuration: 30,
    hasLengthVariants: false, priceShort: "", priceMedium: "", priceLong: "", priceXlong: "",
    onlineBookable: true,  // SVC_ONLINE_TOGGLE_v1
  });
  const [newPhases, setNewPhases] = useState([]); // [{label, duration_min, busy_pct}]
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState("");
  const [phasesSvcId, setPhasesSvcId] = useState(null);  // SERVICE_PHASES_UI_v1
  const [saveState, setSaveState] = useState({});  // SERVICE_SAVE_FIX_v1: {id: "saving"|"ok"|"err"} pro Service

  const categories = _.groupBy(services, "category");

  // SVC_EDIT_NAME_CAT_v1 -- Kategorie-Optionen fürs Edit-Formular: aktuelle Kategorie + vorhandene
  // Salon-Kategorien + Standardliste (Import-Salons haben eigene Kategorienamen, die sonst fehlen würden)
  const svcCatOptions = (cur) => {
    const out = [];
    [cur, ...Object.keys(categories), ...defaultCategories()] // BUSINESS_TYPE_v2 -- Standardliste je Branche
      .forEach(c => { if (c && out.indexOf(c) === -1) out.push(c); });
    return out;
  };

  const updateService = (id, field, value) => {
    setServices(prev => prev.map(s => s.id === id ? { ...s, [field]: typeof s[field] === "number" ? Number(value) : value } : s));
  };

  // SERVICE_SAVE_FIX_v1 -- vorher rief das Edit-Formular NIE das Backend auf (nur lokaler State)
  // -> Preis/Dauer-Aenderungen waren nach Reload weg ("springt zurueck"). Jetzt echter PUT.
  const saveService = async (svc) => {
    // SVC_EDIT_NAME_CAT_v1 -- Name + Kategorie mitspeichern; leerer Name wird abgefangen
    const name = String(svc.name || "").trim();
    if (!name) { setSaveState(m => ({ ...m, [svc.id]: "err" })); return; }
    setSaveState(m => ({ ...m, [svc.id]: "saving" }));
    const res = await api.updateService(svc.id, {
      name: name,
      category: svc.category,
      base_price: Number(svc.basePrice),
      base_duration: Number(svc.baseDuration),
      online_bookable: svc.onlineBookable === false ? 0 : 1,  // SVC_ONLINE_TOGGLE_v1
    });
    if (res && res.ok) {
      setSaveState(m => ({ ...m, [svc.id]: "ok" }));
      setTimeout(() => setSaveState(m => { const n = { ...m }; delete n[svc.id]; return n; }), 2500);
    } else {
      setSaveState(m => ({ ...m, [svc.id]: "err" }));
    }
  };

  const deleteService = async (id) => {
    // SERVICE_SAVE_FIX_v1 -- vorher nur lokaler State (filter) ohne api.deleteService -> Service kam nach Reload zurueck.
    const res = await api.deleteService(id);
    if (res && res.ok) {
      setServices(prev => prev.filter(s => s.id !== id));
      setEditId(null);
    } else {
      alert("Löschen fehlgeschlagen. Bitte erneut versuchen.");
    }
  };

  // SERVICE_CREATE_FULL_v1 -- echte API-Anlage + Phasen
  const addPhaseRow = () => setNewPhases(p => [...p, { label: "", duration_min: 15, busy_pct: 100 }]);
  const updatePhaseRow = (idx, field, value) => setNewPhases(p => p.map((ph, i) => i === idx ? { ...ph, [field]: field === "label" ? value : Number(value) } : ph));
  const removePhaseRow = (idx) => setNewPhases(p => p.filter((_, i) => i !== idx));

  // Auto-Berechnung Gesamtdauer aus Phasen (wenn Phasen definiert)
  const phasesDurationTotal = newPhases.reduce((s, p) => s + (Number(p.duration_min) || 0), 0);

  const addService = async () => {
    setAddError("");
    if (!newSvc.name) { setAddError("Name fehlt"); return; }
    setAddBusy(true);
    try {
      const dur = phasesDurationTotal > 0 ? phasesDurationTotal : Number(newSvc.baseDuration);
      const payload = {
        name: newSvc.name,
        category: newSvc.category,
        subcategory: newSvc.subcategory || null,
        base_price: Number(newSvc.basePrice),
        base_duration: dur,
        has_length_variants: newSvc.hasLengthVariants ? 1 : 0,
        online_bookable: newSvc.onlineBookable === false ? 0 : 1,  // SVC_ONLINE_TOGGLE_v1
        price_short: newSvc.priceShort !== "" ? Number(newSvc.priceShort) : null,
        price_medium: newSvc.priceMedium !== "" ? Number(newSvc.priceMedium) : null,
        price_long: newSvc.priceLong !== "" ? Number(newSvc.priceLong) : null,
        price_xlong: newSvc.priceXlong !== "" ? Number(newSvc.priceXlong) : null,
        phases: newPhases.filter(p => p.label && p.duration_min > 0).map(p => ({
          label: p.label, duration_min: Number(p.duration_min), busy_pct: Number(p.busy_pct),
        })),
      };
      const res = await api.createService(payload);
      if (!res || !res.ok) { setAddError("Speichern fehlgeschlagen: " + ((res && res.data && res.data.error) || "unbekannt")); return; }
      const data = res.data || res;
      const newId = data.id || Math.max(0, ...services.map(s => s.id)) + 1;
      setServices(prev => [...prev, {
        id: newId, name: newSvc.name, category: newSvc.category, subcategory: newSvc.subcategory || null,
        basePrice: Number(newSvc.basePrice), baseDuration: dur,
        hasLengthVariants: newSvc.hasLengthVariants,
        priceShort: payload.price_short, priceMedium: payload.price_medium,
        priceLong: payload.price_long, priceXlong: payload.price_xlong,
        is_active: 1,
        // SVC_MODIFIER_CRASH_FIX_v1 -- gleiche Form wie der API-Loader (DB-Default 1.0),
        // sonst crasht das Buchungsmodal bei Wochenend-Datum mit diesem Service
        priceModifiers: { weekend: 1, seniorStylist: 1 },
        durationModifiers: { longHair: 1, thickHair: 1 },
        onlineBookable: newSvc.onlineBookable !== false,  // SVC_ONLINE_TOGGLE_v1
        phases: payload.phases || [],
      }]);
      setShowAdd(false);
      setNewSvc({ name: "", category: isKosmetikSalon() ? "Gesicht" : "Schnitt", subcategory: "", basePrice: 30, baseDuration: 30,
                  hasLengthVariants: false, priceShort: "", priceMedium: "", priceLong: "", priceXlong: "",
                  onlineBookable: true });
      setNewPhases([]);
    } catch (e) {
      setAddError("Netzwerk-Fehler: " + (e && e.message ? e.message : "unbekannt"));
    }
    setAddBusy(false);
  };

  return (
    <div>
      <Header title="Dienstleistungen" subtitle="Preise bearbeiten, Services hinzufügen" />
      <div style={{ marginBottom: 16 }}>
        <Btn onClick={() => setShowAdd(true)}><Plus size={16} /> Neue Dienstleistung</Btn>
      </div>

      {Object.entries(categories).map(([cat, svcs]) => {
        // SERVICE_AUDIENCE_COLORS_v1 -- Damen/Farbe zart rot, Herren zart blau (gleiche Regel wie die Buchungsseite)
        const tint = svcTintOf(cat);
        return (
        <div key={cat} style={{ marginBottom: 24 }} data-feature={tint ? "SERVICE_AUDIENCE_COLORS_v1" : undefined}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12, color: COLORS.text, display: "flex", alignItems: "center", gap: 8 }}>
            {tint && <span title={tint.label} style={{ width: 9, height: 9, borderRadius: "50%", background: tint.dot, flexShrink: 0 }} />}
            {cat}
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300, 1fr))", gap: 12 }}>
            {svcs.map(svc => (
              <Card key={svc.id} onClick={() => setEditId(svc.id === editId ? null : svc.id)}
                style={tint
                  ? { cursor: "pointer", background: `linear-gradient(${tint.bg}, ${tint.bg}), ${COLORS.card}`, boxShadow: `inset 4px 0 0 ${tint.edge}, var(--shadow-card)` }
                  : { cursor: "pointer" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>{svc.name}</h4>
                  <span style={{ fontSize: 18, fontWeight: 700, color: COLORS.primary }}>{svc.basePrice} €</span>
                </div>
                <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 4 }}>
                  <Clock size={12} style={{ verticalAlign: -2 }} /> {svc.baseDuration} Min. (Basis)
                  {svc.onlineBookable === false && <span style={{ marginLeft: 8, fontWeight: 700 }}>🚫 online aus</span>}
                </div>
                {editId === svc.id && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${COLORS.border}`, fontSize: 12 }} onClick={e => e.stopPropagation()}>
                    {/* SVC_EDIT_NAME_CAT_v1 -- Name + Kategorie bestehender Services änderbar (Kundenwunsch Bio Beauty) */}
                    <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: 8, marginBottom: 10 }}>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 600 }}>Name</label>
                        <input type="text" value={svc.name} onChange={e => updateService(svc.id, "name", e.target.value)}
                          style={{ width: "100%", padding: "6px 8px", border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, boxSizing: "border-box" }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 600 }}>Kategorie</label>
                        <select value={svc.category} onChange={e => updateService(svc.id, "category", e.target.value)}
                          style={{ width: "100%", padding: "6px 8px", border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, boxSizing: "border-box", background: "var(--card)" }}>
                          {svcCatOptions(svc.category).map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: 8, marginBottom: 10 }}>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 600 }}>Basispreis (€)</label>
                        <input type="number" value={svc.basePrice} onChange={e => updateService(svc.id, "basePrice", e.target.value)}
                          style={{ width: "100%", padding: "6px 8px", border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, boxSizing: "border-box" }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 600 }}>Basisdauer (Min.)</label>
                        <input type="number" value={svc.baseDuration} onChange={e => updateService(svc.id, "baseDuration", e.target.value)}
                          style={{ width: "100%", padding: "6px 8px", border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, boxSizing: "border-box" }} />
                      </div>
                    </div>

                    {/* SVC_ONLINE_TOGGLE_v1 (Toby 29.07.) -- Schalter: Leistung online anbieten ja/nein.
                        Aus = erscheint nicht mehr in Online-Formular, Zoey-Chat und KI-Telefon;
                        im Kalender bleibt sie manuell buchbar. */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, padding: "8px 10px", background: "var(--cardHover)", borderRadius: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: svc.onlineBookable === false ? COLORS.textLight : COLORS.success }}>
                        🌐 Online anbieten{svc.onlineBookable === false ? " — aus (nur manuell buchbar)" : ""}
                      </span>
                      <button onClick={() => updateService(svc.id, "onlineBookable", svc.onlineBookable === false)}
                        title="Aus = Leistung erscheint nicht in Online-Buchung, Zoey-Chat und KI-Telefonie; im Kalender weiter manuell buchbar. Danach Speichern klicken!"
                        style={{ width: 40, height: 22, borderRadius: 11, border: "none", cursor: "pointer",
                                 background: svc.onlineBookable === false ? COLORS.border : COLORS.success, position: "relative", transition: "background 0.2s" }}>
                        <div style={{ width: 18, height: 18, borderRadius: "50%", background: "var(--card)",
                                      position: "absolute", top: 2, left: svc.onlineBookable === false ? 2 : 20,
                                      transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                      </button>
                    </div>
                    <div style={{ marginBottom: 6, fontWeight: 600 }}>Preismodifikatoren:</div>
                    <div>Wochenende: ×{svc.priceModifiers.weekend} → <strong>{Math.round(svc.basePrice * svc.priceModifiers.weekend)} €</strong></div>
                    <div>Senior Stylist: ×{svc.priceModifiers.seniorStylist} → <strong>{Math.round(svc.basePrice * svc.priceModifiers.seniorStylist)} €</strong></div>
                    <div style={{ marginTop: 8, marginBottom: 6, fontWeight: 600 }}>Dauermodifikatoren:</div>
                    <div>Lange Haare: ×{svc.durationModifiers.longHair} → <strong>{Math.round(svc.baseDuration * svc.durationModifiers.longHair)} Min.</strong></div>
                    <div>Dickes Haar: ×{svc.durationModifiers.thickHair} → <strong>{Math.round(svc.baseDuration * svc.durationModifiers.thickHair)} Min.</strong></div>
                    <div style={{ marginTop: 10, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                      <Btn size="sm" onClick={() => saveService(svc)} disabled={saveState[svc.id] === "saving"}>{saveState[svc.id] === "saving" ? "Speichert…" : "Speichern"}</Btn>
                      {saveState[svc.id] === "ok" && <span style={{ color: "#16a34a", fontSize: 12, fontWeight: 600 }}>✓ Gespeichert</span>}
                      {saveState[svc.id] === "err" && <span style={{ color: "#dc2626", fontSize: 12, fontWeight: 600 }}>Fehler – nicht gespeichert</span>}
                      <Btn size="sm" variant="secondary" onClick={() => setPhasesSvcId(svc.id)}><Clock size={12} /> Phasen</Btn>
                      <Btn size="sm" variant="danger" onClick={() => deleteService(svc.id)}><Trash2 size={12} /> Löschen</Btn>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
        );
      })}

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Neue Dienstleistung hinzufügen" width={640}>
        <Input label="Name" value={newSvc.name} onChange={e => setNewSvc(n => ({ ...n, name: e.target.value }))} placeholder={isKosmetikSalon() ? "z.B. Gesichtsbehandlung Classic" : "z.B. Keratin-Behandlung"} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {/* BUSINESS_TYPE_v2 -- Kategorien je Branche */}
          <Select label="Kategorie" value={newSvc.category} onChange={e => setNewSvc(n => ({ ...n, category: e.target.value }))} options={
            defaultCategories().map(c => ({ value: c, label: c }))
          } />
          <Input label="Subkategorie (optional)" value={newSvc.subcategory} onChange={e => setNewSvc(n => ({ ...n, subcategory: e.target.value }))} placeholder={isKosmetikSalon() ? "z.B. Anti-Aging" : "z.B. Colorationen"} />
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <Input label="Basispreis (€)" type="number" value={newSvc.basePrice} onChange={e => setNewSvc(n => ({ ...n, basePrice: e.target.value }))} />
          <Input label={`Basisdauer (Min.${phasesDurationTotal > 0 ? " — auto aus Phasen" : ""})`} type="number" value={phasesDurationTotal > 0 ? phasesDurationTotal : newSvc.baseDuration} disabled={phasesDurationTotal > 0} onChange={e => setNewSvc(n => ({ ...n, baseDuration: e.target.value }))} />
        </div>

        {/* SVC_ONLINE_TOGGLE_v1 -- Schalter auch beim Anlegen */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 6, marginBottom: 6, padding: "8px 10px", background: "var(--cardHover)", borderRadius: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: newSvc.onlineBookable === false ? COLORS.textLight : COLORS.success }}>
            🌐 Online anbieten{newSvc.onlineBookable === false ? " — aus (nur manuell buchbar)" : ""}
          </span>
          <button onClick={() => setNewSvc(n => ({ ...n, onlineBookable: n.onlineBookable === false }))}
            title="Aus = Leistung erscheint nicht in Online-Buchung, Zoey-Chat und KI-Telefonie; im Kalender weiter manuell buchbar."
            style={{ width: 40, height: 22, borderRadius: 11, border: "none", cursor: "pointer",
                     background: newSvc.onlineBookable === false ? COLORS.border : COLORS.success, position: "relative", transition: "background 0.2s" }}>
            <div style={{ width: 18, height: 18, borderRadius: "50%", background: "var(--card)",
                          position: "absolute", top: 2, left: newSvc.onlineBookable === false ? 2 : 20,
                          transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
          </button>
        </div>

        {/* SERVICE_CREATE_FULL_v1 -- Längen-Preise (4 Stufen)
            BUSINESS_TYPE_v2 -- bei Kosmetikstudios ausgeblendet */}
        {!isKosmetikSalon() && <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginTop: 6, marginBottom: 6, cursor: "pointer" }}>
          <input type="checkbox" checked={newSvc.hasLengthVariants} onChange={e => setNewSvc(n => ({ ...n, hasLengthVariants: e.target.checked }))} />
          Preis variiert nach Haarlänge (S/M/L/X-Lang)
        </label>}
        {newSvc.hasLengthVariants && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 10, padding: 10, background: "var(--cardHover)", borderRadius: 8 }}>
            <Input label="Kurz €" type="number" value={newSvc.priceShort} onChange={e => setNewSvc(n => ({ ...n, priceShort: e.target.value }))} />
            <Input label="Mittel €" type="number" value={newSvc.priceMedium} onChange={e => setNewSvc(n => ({ ...n, priceMedium: e.target.value }))} />
            <Input label="Lang €" type="number" value={newSvc.priceLong} onChange={e => setNewSvc(n => ({ ...n, priceLong: e.target.value }))} />
            <Input label="X-Lang €" type="number" value={newSvc.priceXlong} onChange={e => setNewSvc(n => ({ ...n, priceXlong: e.target.value }))} />
          </div>
        )}

        {/* SERVICE_CREATE_FULL_v1 -- Phasen-Editor inline */}
        <div style={{ marginTop: 10, padding: 12, background: "var(--cardHover)", borderRadius: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <strong style={{ fontSize: 13 }}>Phasen (optional)</strong>
            <Btn size="sm" variant="secondary" onClick={addPhaseRow}><Plus size={12} /> Phase</Btn>
          </div>
          <div style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 10 }}>
            Service in Arbeitsschritte unterteilen (z.B. Auftragen → Einwirkzeit → Auswaschen). Einwirkzeiten (busy=0) erlauben Doppelbuchungen.
          </div>
          {newPhases.length === 0 && <div style={{ fontSize: 12, color: COLORS.textLight, fontStyle: "italic", padding: "8px 0" }}>Keine Phasen — Service wird als Einzelblock gebucht.</div>}
          {newPhases.map((p, idx) => (
            <div key={idx} style={{ display: "grid", gridTemplateColumns: "30px 1fr 90px 110px 30px", gap: 6, alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: COLORS.textLight, textAlign: "center" }}>#{idx + 1}</span>
              <input value={p.label} onChange={e => updatePhaseRow(idx, "label", e.target.value)} placeholder="z.B. Auftragen" style={{ padding: "6px 8px", borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 12, background: "var(--card)", color: COLORS.text, boxSizing: "border-box" }} />
              <input type="number" value={p.duration_min} onChange={e => updatePhaseRow(idx, "duration_min", e.target.value)} placeholder="Min" style={{ padding: "6px 8px", borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 12, background: "var(--card)", color: COLORS.text, boxSizing: "border-box" }} />
              <select value={p.busy_pct} onChange={e => updatePhaseRow(idx, "busy_pct", e.target.value)} style={{ padding: "6px 8px", borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 12, background: "var(--card)", color: COLORS.text }}>
                <option value={100}>Aktiv (Mitarbeiter arbeitet)</option>
                <option value={0}>Einwirkzeit (Mitarbeiter frei)</option>
              </select>
              <button onClick={() => removePhaseRow(idx)} style={{ background: "none", border: "none", color: COLORS.danger, cursor: "pointer", padding: 4 }}><Trash2 size={12} /></button>
            </div>
          ))}
          {phasesDurationTotal > 0 && (
            <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 6, textAlign: "right" }}>
              Gesamtdauer aus Phasen: <strong>{phasesDurationTotal} Min</strong>
            </div>
          )}
        </div>

        {addError && <div style={{ marginTop: 10, padding: 8, background: "rgba(239,68,68,0.10)", color: COLORS.danger, borderRadius: 6, fontSize: 12 }}>{addError}</div>}
        <div style={{ marginTop: 14 }}>
          <Btn variant="success" onClick={addService} disabled={!newSvc.name || addBusy}><Check size={14} /> {addBusy ? "Speichere..." : "Hinzufügen"}</Btn>
        </div>
      </Modal>
      {phasesSvcId !== null && (() => { const _svc = services.find(s => s.id === phasesSvcId); return <PhasesEditor serviceId={phasesSvcId} serviceName={_svc ? _svc.name : ""} baseDuration={_svc ? _svc.baseDuration : 0} onClose={() => setPhasesSvcId(null)}
        /* PHASE_EDITOR_SYNC_v1 -- neue Dauer + Phasen sofort in die Liste übernehmen (auch Kalender-Overlay nutzt svc.phases) */
        onSaved={(newDur, newPhases) => setServices(prev => prev.map(s => s.id === phasesSvcId ? { ...s, ...(newDur ? { baseDuration: newDur } : {}), phases: (newPhases || []).filter(p => p.is_active !== false).map(p => ({ phase_no: p.phase_no, start_offset_min: p.start_offset_min, duration_min: p.duration_min, busy_pct: p.busy_pct, label: p.label })) } : s))} />; })()}
    </div>
  );
};

// ============================================================
// TIER_PRICING_UI_v1 -- Tier-basierte Preise pro Mitarbeiter-Klasse
// Top-level Component (KEIN inline-Pattern). Backend-Endpoints schon vorhanden:
//   GET /api/pricing/matrix → Tiers + Services + Prices + Employees-mit-Tier
//   PUT /api/pricing/matrix/<service_id>/<tier_id> body {price_eur, duration_min?}
//   PUT /api/employees/<id>/tier body {pricing_tier_id}
// Preis-Lock beim Buchen: appointment_services.price_eur wird zum Zeitpunkt der Buchung
// gespeichert → bleibt bei Mitarbeiter-Wechsel gleich (Tobys Anforderung 20.05.).
// ============================================================
export const TierPricingTab = ({ employees = [], services = [] }) => {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingCell, setSavingCell] = useState(null); // "svcId-tierId"
  const [statusMsg, setStatusMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.request("/pricing/matrix");
        if (cancelled) return;
        if (r && r.ok) setMatrix(r.data);
        setLoading(false);
      } catch (e) { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const priceFor = (svcId, tierId) => {
    if (!matrix || !matrix.prices) return null;
    const p = matrix.prices.find(x => x.service_id === svcId && x.tier_id === tierId);
    return p ? p.price_eur : null;
  };

  // TIER_DURATION_UI_v1 -- Dauer je Stufe (null = Dauer der Dienstleistung gilt).
  // Backend (TierMatrixHandler/TierPriceUpsertHandler) liefert und nimmt duration_min
  // schon laenger; ohne UI war der Wert unsichtbar und steuerte trotzdem Online-Buchung,
  // Zoey und KI-Telefon (Fall Salon Wolf 17.08.: Kurzhaarschnitt 60 Min im Kalender,
  // 45 Min online). WICHTIG: der PUT ueberschreibt IMMER beide Felder -- beim Speichern
  // eines Preises muss die bestehende Dauer mitgeschickt werden, sonst faellt sie auf NULL.
  const durationFor = (svcId, tierId) => {
    if (!matrix || !matrix.prices) return null;
    const p = matrix.prices.find(x => x.service_id === svcId && x.tier_id === tierId);
    return p && p.duration_min != null ? Number(p.duration_min) : null;
  };

  const handlePriceBlur = async (svcId, tierId, val) => {
    if (!matrix) return;
    const trimmed = (val || "").toString().trim().replace(",", ".");
    if (!trimmed) return;
    const num = parseFloat(trimmed);
    if (isNaN(num) || num < 0) {
      setStatusMsg("Ungültiger Preis");
      setTimeout(() => setStatusMsg(""), 2000);
      return;
    }
    const cellKey = svcId + "-" + tierId;
    const keepDur = durationFor(svcId, tierId); // TIER_DURATION_UI_v1 -- Dauer nicht verlieren
    setSavingCell(cellKey);
    const r = await api.request("/pricing/matrix/" + svcId + "/" + tierId, {
      method: "PUT", body: JSON.stringify({ price_eur: num, duration_min: keepDur })
    });
    setSavingCell(null);
    if (r && r.ok) {
      setMatrix(m => {
        const filtered = m.prices.filter(x => !(x.service_id === svcId && x.tier_id === tierId));
        return { ...m, prices: [...filtered, { service_id: svcId, tier_id: tierId, price_eur: num, duration_min: keepDur }] };
      });
      setStatusMsg("✓ Gespeichert");
      setTimeout(() => setStatusMsg(""), 1500);
    } else {
      setStatusMsg("Fehler beim Speichern");
      setTimeout(() => setStatusMsg(""), 2500);
    }
  };

  // TIER_DURATION_UI_v1 -- Dauer speichern. Leeres Feld = NULL = Dauer der Dienstleistung.
  // Ohne Preis in der Zelle geht es nicht: der Backend-PUT verlangt price_eur als Zahl.
  const handleDurationBlur = async (svcId, tierId, val) => {
    if (!matrix) return;
    const trimmed = (val || "").toString().trim();
    const cur = durationFor(svcId, tierId);
    let num = null;
    if (trimmed) {
      num = parseInt(trimmed.replace(",", "."), 10);
      if (isNaN(num) || num <= 0 || num > 600) {
        setStatusMsg("Dauer muss zwischen 1 und 600 Minuten liegen");
        setTimeout(() => setStatusMsg(""), 2500);
        return;
      }
    }
    if ((num == null && cur == null) || num === cur) return; // nichts geaendert
    const price = priceFor(svcId, tierId);
    if (price == null) {
      setStatusMsg("Erst einen Preis für diese Stufe eintragen");
      setTimeout(() => setStatusMsg(""), 2800);
      return;
    }
    const cellKey = svcId + "-" + tierId + "-d";
    setSavingCell(cellKey);
    const r = await api.request("/pricing/matrix/" + svcId + "/" + tierId, {
      method: "PUT", body: JSON.stringify({ price_eur: price, duration_min: num })
    });
    setSavingCell(null);
    if (r && r.ok) {
      setMatrix(m => {
        const filtered = m.prices.filter(x => !(x.service_id === svcId && x.tier_id === tierId));
        return { ...m, prices: [...filtered, { service_id: svcId, tier_id: tierId, price_eur: price, duration_min: num }] };
      });
      setStatusMsg("✓ Gespeichert");
      setTimeout(() => setStatusMsg(""), 1500);
    } else {
      setStatusMsg("Fehler beim Speichern");
      setTimeout(() => setStatusMsg(""), 2500);
    }
  };

  const handleEmployeeTierChange = async (empId, tierId) => {
    const r = await api.request("/employees/" + empId + "/tier", {
      method: "PUT",
      body: JSON.stringify({ pricing_tier_id: tierId === "" ? null : Number(tierId) })
    });
    if (r && r.ok) {
      setMatrix(m => ({
        ...m,
        employees: m.employees.map(e => e.id === empId ? { ...e, pricing_tier_id: tierId === "" ? null : Number(tierId) } : e)
      }));
      setStatusMsg("✓ Zuordnung gespeichert");
      setTimeout(() => setStatusMsg(""), 1500);
    } else {
      setStatusMsg("Fehler bei Zuordnung");
      setTimeout(() => setStatusMsg(""), 2500);
    }
  };

  if (loading) return <div style={{ padding: 30, textAlign: "center", color: COLORS.textLight }}>Lade...</div>;
  if (!matrix) return <div style={{ padding: 30, textAlign: "center", color: COLORS.textLight }}>Keine Daten</div>;

  const tiers = matrix.tiers || [];
  const svcs = matrix.services || [];
  const emps = matrix.employees || [];

  return (
    <div>
      <div style={{ marginBottom: 16, padding: 12, background: "rgba(124,58,237,0.08)", border: "1px solid rgba(124,58,237,0.25)", borderRadius: 8, fontSize: 12.5, lineHeight: 1.5, color: COLORS.text }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>💡 So funktioniert's</div>
        Jeder Mitarbeiter gehört einer {isKosmetikSalon() ? "Erfahrungs-Stufe" : "Stylist-Stufe (Junior, Stylist, Senior, Master)"} — du bestimmst pro Service einen Preis je Stufe.
        Online-Buchung, Zoey-Chat und KI-Telefonie zeigen automatisch den richtigen Preis je nach gewähltem Mitarbeiter.
        <br /><strong>Wichtig:</strong> der Preis wird beim Buchen festgenagelt — wenn das Salon-Team später zu einem anderen Mitarbeiter wechselt, bleibt der ursprüngliche Preis. Der Kunde zahlt nie mehr.
      </div>

      {statusMsg && (
        <div style={{ position: "fixed", top: 80, right: 20, padding: "8px 16px", background: statusMsg.startsWith("✓") ? "#10B981" : "#EF4444", color: "white", borderRadius: 8, fontSize: 13, fontWeight: 600, zIndex: 9999, boxShadow: "0 4px 12px rgba(0,0,0,0.2)" }}>
          {statusMsg}
        </div>
      )}

      {tiers.length === 0 ? (
        <Card>
          <div style={{ padding: 20, textAlign: "center", color: COLORS.textLight }}>
            Noch keine {isKosmetikSalon() ? "Erfahrungs-Stufen" : "Stylist-Stufen"} angelegt. Admin muss das in der DB initialisieren (pricing_tiers-Tabelle).
          </div>
        </Card>
      ) : (
        <>
          {/* Mitarbeiter-Zuordnung */}
          <Card style={{ padding: 0, overflow: "hidden", marginBottom: 20 }}>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid " + COLORS.border, fontSize: 14, fontWeight: 700 }}>Mitarbeiter → Stufe</div>
            <div style={{ padding: "8px 16px" }}>
              {emps.map(e => (
                <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: "1px solid " + COLORS.border }}>
                  <div style={{ flex: 1, fontSize: 14, fontWeight: 500, color: COLORS.text }}>{e.name}</div>
                  <select value={e.pricing_tier_id || ""} onChange={evt => handleEmployeeTierChange(e.id, evt.target.value)}
                    style={{ padding: "6px 10px", border: "1px solid " + COLORS.border, borderRadius: 6, fontSize: 13, background: "var(--card)", color: COLORS.text, minWidth: 160 }}>
                    <option value="">— keine Stufe —</option>
                    {tiers.map(t => <option key={t.id} value={t.id}>{t.display_name}</option>)}
                  </select>
                </div>
              ))}
              {emps.length === 0 && <div style={{ padding: 16, color: COLORS.textLight, fontSize: 13 }}>Keine Mitarbeiter</div>}
            </div>
          </Card>

          {/* Preis-Matrix */}
          <Card style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid " + COLORS.border, fontSize: 14, fontWeight: 700 }}>
              Preis-Matrix · Service × {isKosmetikSalon() ? "Erfahrungs-Stufe" : "Stylist-Stufe"}
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "var(--cardHover)" }}>
                    <th style={{ padding: "10px 14px", textAlign: "left", fontSize: 12, fontWeight: 600, color: COLORS.textLight, position: "sticky", left: 0, background: "var(--cardHover)" }}>Service</th>
                    <th style={{ padding: "10px 14px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textLight }}>Basis</th>
                    {tiers.map(t => (
                      <th key={t.id} style={{ padding: "10px 14px", textAlign: "right", fontSize: 12, fontWeight: 700, color: COLORS.text }}>{t.display_name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {svcs.map(s => (
                    <tr key={s.id} style={{ borderBottom: "1px solid " + COLORS.border }}>
                      <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 500, position: "sticky", left: 0, background: "var(--card)" }}>
                        <div>{s.name}</div>
                        <div style={{ fontSize: 11, color: COLORS.textLight }}>{s.category || ""}</div>
                      </td>
                      <td style={{ padding: "10px 14px", fontSize: 12, color: COLORS.textLight, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {s.base_price} €
                      </td>
                      {tiers.map(t => {
                        const p = priceFor(s.id, t.id);
                        const d = durationFor(s.id, t.id); // TIER_DURATION_UI_v1
                        const cellKey = s.id + "-" + t.id;
                        return (
                          <td key={t.id} style={{ padding: "6px 10px", textAlign: "right" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                              <input
                                type="text"
                                defaultValue={p != null ? p.toString().replace(".", ",") : ""}
                                key={cellKey + "-" + (p != null ? p : "x")}
                                onBlur={e => handlePriceBlur(s.id, t.id, e.target.value)}
                                placeholder="—"
                                style={{
                                  width: 80, padding: "5px 8px", borderRadius: 6,
                                  border: "1px solid " + (savingCell === cellKey ? "#7C3AED" : COLORS.border),
                                  background: "var(--card)", color: COLORS.text, fontSize: 13,
                                  textAlign: "right", fontVariantNumeric: "tabular-nums",
                                  boxSizing: "border-box",
                                }}
                              />
                              <span style={{ fontSize: 12, color: COLORS.textLight }}>€</span>
                            </div>
                            {/* TIER_DURATION_UI_v1 -- Dauer je Stufe. Leer = Dauer der Dienstleistung (Platzhalter zeigt sie). */}
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4, marginTop: 4 }}>
                              <input
                                type="text"
                                inputMode="numeric"
                                defaultValue={d != null ? String(d) : ""}
                                key={cellKey + "-d-" + (d != null ? d : "x")}
                                onBlur={e => handleDurationBlur(s.id, t.id, e.target.value)}
                                placeholder={s.base_duration != null ? String(s.base_duration) : "—"}
                                title="Dauer für diese Stufe. Leer = Dauer der Dienstleistung."
                                style={{
                                  width: 80, padding: "4px 8px", borderRadius: 6,
                                  border: "1px dashed " + (savingCell === cellKey + "-d" ? "#7C3AED" : COLORS.border),
                                  background: "var(--card)", color: d != null ? COLORS.text : COLORS.textLight, fontSize: 12,
                                  textAlign: "right", fontVariantNumeric: "tabular-nums",
                                  boxSizing: "border-box",
                                }}
                              />
                              <span style={{ fontSize: 11, color: COLORS.textLight }}>Min</span>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: "10px 16px", borderTop: "1px solid " + COLORS.border, fontSize: 11, color: COLORS.textLight }}>
              Tipp: Wert eintippen und aus der Zelle klicken (Tab oder Enter) → wird gespeichert. Oben der Preis, darunter die Dauer in Minuten.
              Beides leer lassen = es gilt der Basis-Preis bzw. die Dauer der Dienstleistung (grau im Feld angezeigt).
              Eine Dauer hier lohnt sich, wenn jemand für dieselbe Leistung schneller oder langsamer ist — zum Beispiel 20 Minuten beim Meister statt 30 bei der Junior-Stylistin. Sie gilt dann überall: im Kalender, bei der Online-Buchung, im Chat und am KI-Telefon. Bleibt das Feld leer, zählt die Dauer aus der Dienstleistung.
            </div>
          </Card>
        </>
      )}
    </div>
  );
};